import React, { useContext, useRef, useState } from 'react';
import { BACKEND_URL } from '../config';
import AppContext from '../AppContext';
import { useLanguage } from '../contexts/LanguageContext';

// Shared row of controls for a single product: ⭐ feature toggle + 📷 photo upload/remove.
// Used by both ProductManagement and DigitalMenuSettings — single source of truth for the
// upload handler so both screens stay in sync.
//
// Props:
//   product: { id, name, image, is_featured, ... } — current row from the API
//   onChanged: () => void — called after a successful change so the parent can refetch
//
function ProductFeaturedControls({ product, onChanged }) {
  const { theme } = useContext(AppContext);
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const hasImage = !!product.image;
  const isFeatured = !!product.is_featured;
  const cacheKey = product.imageVersion
    || (product.modified ? new Date(product.modified).getTime() : '')
    || '';
  const imgSrc = hasImage
    ? `${BACKEND_URL}/api/products/${product.id}/image?v=${cacheKey}`
    : null;

  const colors = theme?.colors || {};
  const featuredOn = colors.warning || '#f6b100';
  const featuredOff = colors.textSecondary || '#999';
  const border = colors.border || '#ccc';

  const toggleFeatured = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_id: product.category_id,
          name: product.name,
          description: product.description,
          description_español: product.description_español || product.description_es || '',
          active: product.active !== undefined ? product.active : 1,
          ordinal: product.ordinal || 1,
          cost: product.cost || 0,
          price: product.price,
          is_featured: !isFeatured,
        }),
      });
      if (!res.ok) throw new Error('Failed to update featured flag');
      onChanged && onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setError('File must be under 8MB');
      e.target.value = '';
      return;
    }
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch(`${BACKEND_URL}/api/products/${product.id}/image`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Upload failed');
      }
      onChanged && onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      e.target.value = '';
    }
  };

  const removeImage = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/${product.id}/image`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove image');
      onChanged && onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <button
        type="button"
        onClick={toggleFeatured}
        disabled={busy}
        title={isFeatured ? t('markAsFeatured') : t('markAsFeatured')}
        style={{
          width: 32, height: 32, padding: 0,
          border: `1px solid ${border}`,
          borderRadius: theme?.borderRadius || 4,
          backgroundColor: isFeatured ? `${featuredOn}22` : 'transparent',
          color: isFeatured ? featuredOn : featuredOff,
          cursor: busy ? 'wait' : 'pointer',
          fontSize: '1rem', lineHeight: 1,
        }}
      >★</button>

      {hasImage ? (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <img
            src={imgSrc}
            alt=""
            style={{
              width: 40, height: 40, objectFit: 'cover',
              borderRadius: theme?.borderRadius || 4,
              border: `1px solid ${border}`,
              display: 'block',
            }}
          />
          <button
            type="button"
            onClick={removeImage}
            disabled={busy}
            title={t('removePhoto')}
            style={{
              position: 'absolute', top: -6, right: -6,
              width: 18, height: 18, padding: 0, lineHeight: '14px',
              border: `1px solid ${border}`,
              borderRadius: '50%',
              backgroundColor: colors.surface || '#fff',
              color: colors.error || '#c62828',
              cursor: busy ? 'wait' : 'pointer',
              fontSize: '0.75rem', fontWeight: 'bold',
            }}
          >×</button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={busy}
          title={t('addPhoto')}
          style={{
            width: 32, height: 32, padding: 0,
            border: `1px dashed ${border}`,
            borderRadius: theme?.borderRadius || 4,
            backgroundColor: 'transparent',
            color: colors.textSecondary || '#999',
            cursor: busy ? 'wait' : 'pointer',
            fontSize: '0.95rem',
          }}
        >📷</button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        onChange={onFile}
        style={{ display: 'none' }}
      />

      {error && <span style={{ fontSize: '0.7rem', color: colors.error || '#c62828' }}>{error}</span>}
    </div>
  );
}

export default ProductFeaturedControls;
